import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useProgramConfig } from '../config/useProgramConfig';
import { useTranslation } from '../i18n';
import './GlossaryTerm.css';

interface Props {
  termKey: string;
  children: ReactNode;
}

/** Underlined span with an accessible tooltip (desktop) / bottom sheet
 *  (mobile). Pulls its definition from the program config — the LLM must
 *  never author the definition. */
export function GlossaryTerm({ termKey, children }: Props) {
  const config = useProgramConfig();
  const { localized, t } = useTranslation();
  const entry = config.glossary[termKey];

  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<'tooltip' | 'sheet'>('tooltip');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Responsive mode: below 768px use bottom sheet; desktop uses tooltip.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => setPlacement(mq.matches ? 'sheet' : 'tooltip');
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // Close on Escape; close on outside click.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  if (!entry) {
    // Unknown key — render as plain text so we never break the reply.
    return <>{children}</>;
  }

  const termText = localized(entry.term);
  const definitionText = localized(entry.definition);

  return (
    <span className="cf-glossary">
      <button
        ref={triggerRef}
        type="button"
        className="cf-glossary-term"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => placement === 'tooltip' && setOpen(true)}
        onMouseLeave={() => placement === 'tooltip' && setOpen(false)}
        onFocus={() => placement === 'tooltip' && setOpen(true)}
        onBlur={() => placement === 'tooltip' && setOpen(false)}
        aria-expanded={open}
        aria-describedby={open ? `cf-glossary-def-${termKey}` : undefined}
        aria-label={`${termText} — ${t('a11y.glossary_term_has_definition')}`}
      >
        {children}
      </button>

      {open && placement === 'tooltip' && (
        <span
          ref={popoverRef}
          id={`cf-glossary-def-${termKey}`}
          className="cf-glossary-tooltip"
          role="tooltip"
        >
          <strong className="cf-glossary-tooltip__term">{termText}</strong>
          <span className="cf-glossary-tooltip__def">{definitionText}</span>
        </span>
      )}

      {open && placement === 'sheet' && (
        <div
          className="cf-glossary-sheet-backdrop"
          role="presentation"
          onClick={() => setOpen(false)}
        >
          <div
            ref={popoverRef}
            id={`cf-glossary-def-${termKey}`}
            className="cf-glossary-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={termText}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="cf-glossary-sheet__term">{termText}</h3>
            <p className="cf-glossary-sheet__def">{definitionText}</p>
            <button
              type="button"
              className="cf-btn cf-btn--ghost"
              onClick={() => setOpen(false)}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </span>
  );
}
